import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Sparkles, Zap, Shield, Target, Palette, Flag } from "lucide-react";
import PricingCard from "../common/PricingCard";
import FeatureCard from "../common/FeatureCard";
import Footer from "../common/Footer";
import { LOGO_URL, BRAND } from "../common/constants";

const ResumeLandingPage = ({ onGetStarted, onSelectPackage }) => {
  const navigate = useNavigate();


  const handlePackageSelect = (pkg) => {
    onSelectPackage(pkg);
    onGetStarted();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND.parent} className="h-12 w-auto rounded-md" />
            <span className="hidden sm:block font-heading font-bold text-[#071734]">{BRAND.productSuite}</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/veterans" className="text-sm text-red-600 hover:text-red-500 hidden sm:flex items-center gap-1">
              <Flag className="w-3 h-3" /> We Honor Those Who Serve
            </Link>
            <Button 
              data-testid="nav-get-started-btn"
              onClick={() => handlePackageSelect("complete")}
              className="bg-[#00a3e0] hover:bg-[#0085b8] text-white"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Ethical AI Promise - NOW AT TOP */}
      <section className="pt-24 pb-12">
        <div className="container-main">
          <Card className="bg-gradient-to-r from-[#00a3e0]/5 via-[#071734]/5 to-[#00a3e0]/5 border-[#00a3e0]/30">
            <CardContent className="py-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <Shield className="w-16 h-16 text-[#00a3e0] flex-shrink-0" />
                <div className="text-center md:text-left">
                  <h2 className="text-2xl font-heading font-bold mb-2 text-[#071734]">Our Ethical AI Promise</h2>
                  <p className="text-gray-600">
                    {BRAND.parent} will <strong className="text-[#071734]">never</strong> fabricate degrees, inflate job titles, 
                    or invent achievements. We enhance how you present your <em>real</em> experience — 
                    nothing more, nothing less. What you get is authentic, professional, and 100% based on YOUR input.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00a3e0]/5 to-transparent pointer-events-none" />
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="outline" className="border-[#00a3e0]/50 text-[#00a3e0] px-4 py-1.5 bg-[#00a3e0]/5">
              <Zap className="w-3 h-3 mr-1.5" /> AI-Powered Career Solutions
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight text-[#071734]">
              Land More Interviews with{" "}
              <span className="text-[#00a3e0]">
                AI-Optimized
              </span>{" "}
              Resumes
            </h1>
            
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              High-impact, concise resumes that pass ATS filters and impress recruiters.
              Choose your package below to get started.
            </p>

            <Link 
              to="/veterans" 
              className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-500"
            >
              <Flag className="w-4 h-4" />
              We Honor Those Who Serve — Click here for exclusive discounts
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section - Primary CTA */}
      <section id="pricing" className="py-12">
        <div className="container-main">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-heading font-bold mb-2 text-[#071734]">Choose Your Package</h2>
            <p className="text-gray-600">Select a package to continue to the resume builder</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <PricingCard 
              title="Essential"
              price="$9.99"
              description="Quick resume optimization"
              features={[
                "ATS-optimized resume",
                "Tailored cover letter",
                "Match score analysis",
                "DOCX downloads"
              ]}
              onSelect={() => handlePackageSelect("basic")}
            />
            <PricingCard 
              title="Complete"
              price="$19.99"
              description="Full interview prep"
              features={[
                "ATS-optimized resume",
                "Tailored cover letter",
                "Professional CV",
                "Interview practice guide",
                "DOCX downloads"
              ]}
              popular={true}
              onSelect={() => handlePackageSelect("complete")}
            />
            <PricingCard 
              title="Career Profiler"
              price="$29.99"
              description="Full career transition package"
              features={[
                "Everything in Complete",
                { main: "Career Narrative", sub: ["Skill translation analysis", "Career positioning strategy"] },
                { main: "Job Strategy Guide", sub: ["Ideal role recommendations", "Job search strategy", "Platform-specific guidance"] }
              ]}
              premium={true}
              onSelect={() => handlePackageSelect("career_profiler")}
              savings="Save $500+ vs career coaching"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-gray-50">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={<FileText className="w-6 h-6" />}
              title="ATS-Optimized"
              description="Keywords from the job description automatically integrated"
            />
            <FeatureCard 
              icon={<Target className="w-6 h-6" />}
              title="High-Impact Format"
              description="Concise, high-impact resumes with powerful bullet points"
            />
            <FeatureCard 
              icon={<Palette className="w-6 h-6" />}
              title="Custom Styling"
              description="6 color schemes and multiple layouts to choose from"
            />
          </div>
        </div>
      </section>

      <Footer variant="simple" />
    </div>
  );
};

export default ResumeLandingPage;
